import {useState} from 'react';
import {useIntl} from 'react-intl';
import {useQueryClient} from 'react-query';
import {Button as BootstrapButton, Card, Form} from 'react-bootstrap';
import Button from '../../../../../Components/Button';
import {
  CLIR_ASSIGNATIONS_QUERY_KEY,
  useChangerAccessCode,
  useGetAccessCodeStatus,
  useGetClirAssignations,
  useGetUtilisateursNumeroCourtInvalide,
  useGetVirtualPbxActivite,
  useGetVirtualPbxNumeros,
} from '../../../Hooks/TelephonieManager';
import Loading from '../../../../../app/common/components/Loading';

/**
 * MAQUETTE — reproduction de « OutgoingCalls.aspx » : deux fonctionnalités
 * indépendantes sur la même page (Access Code + affichage du numéro sortant/CLIR).
 * Le wizard Access Code est simplifié en une machine à états explicite (5 étapes),
 * comme le recommande l'analyse du legacy, plutôt que le show/hide DOM en cascade
 * d'origine.
 */

const NUMERO_COURT_REGEX = /^[2-9][0-9]{3}$/;

type EtapeAccessCode = 'choix' | 'verification' | 'recapitulatif' | 'confirmation';

const AccessCodeWizard = (): JSX.Element | null => {
  const intl = useIntl();
  const {data: statut, isLoading: isLoadingStatut} = useGetAccessCodeStatus();
  const {data: utilisateursInvalides, isLoading: isLoadingUtilisateurs} =
    useGetUtilisateursNumeroCourtInvalide();
  const {mutate: changerAccessCode} = useChangerAccessCode();
  const [etape, setEtape] = useState<EtapeAccessCode>('choix');
  const [valeurs, setValeurs] = useState<Record<number, string>>({});
  const [erreur, setErreur] = useState<string | null>(null);
  const [accepte, setAccepte] = useState(false);
  const [futurStatut, setFuturStatut] = useState(false);

  if (isLoadingStatut || !statut || isLoadingUtilisateurs || !utilisateursInvalides) {
    return <Loading className="col-md-6" />;
  }

  const choisir = (actif: boolean): void => {
    if (actif === statut.actif) {
      return;
    }
    setFuturStatut(actif);
    if (!actif) {
      changerAccessCode({actif: false, changements: []});
      setEtape('confirmation');
      return;
    }
    if (utilisateursInvalides.length === 0) {
      setEtape('recapitulatif');
      return;
    }
    setValeurs(
      Object.fromEntries(utilisateursInvalides.map((u) => [u.id, u.numeroCourtSuggere]))
    );
    setEtape('verification');
  };

  const validerVerification = (): void => {
    const saisies = Object.values(valeurs);
    if (!saisies.every((valeur) => NUMERO_COURT_REGEX.test(valeur))) {
      setErreur(intl.formatMessage({id: 'TELEPHONIE.PBX.SORTANTS.ACCESSCODE.ERREUR_FORMAT'}));
      return;
    }
    if (new Set(saisies).size !== saisies.length) {
      setErreur(intl.formatMessage({id: 'TELEPHONIE.PBX.SORTANTS.ACCESSCODE.ERREUR_DOUBLON'}));
      return;
    }
    setErreur(null);
    setEtape('recapitulatif');
  };

  const confirmer = (): void => {
    changerAccessCode({
      actif: futurStatut,
      changements: utilisateursInvalides.map((u) => ({id: u.id, numeroCourt: valeurs[u.id]})),
    });
    setEtape('confirmation');
  };

  const terminer = (): void => {
    setEtape('choix');
    setAccepte(false);
    setErreur(null);
  };

  return (
    <Card bg="light" className="mb-5">
      <Card.Body>
        <div className="fw-bold text-uppercase fs-6 mb-2">
          {intl.formatMessage({id: 'TELEPHONIE.PBX.SORTANTS.ACCESSCODE.TITLE'})}
        </div>
        <p className="text-muted fs-7 mb-4">
          {intl.formatMessage({id: 'TELEPHONIE.PBX.SORTANTS.ACCESSCODE.DESCRIPTION'})}
        </p>

        {etape === 'choix' && (
          <div className="d-flex gap-4">
            <Form.Check
              type="radio"
              id="access-code-oui"
              label={intl.formatMessage({id: 'TELEPHONIE.PBX.GROUPES.APPELS_REPONDUS.ACTIF'})}
              checked={statut.actif}
              onChange={() => choisir(true)}
            />
            <Form.Check
              type="radio"
              id="access-code-non"
              label={intl.formatMessage({id: 'TELEPHONIE.PBX.GROUPES.APPELS_REPONDUS.INACTIF'})}
              checked={!statut.actif}
              onChange={() => choisir(false)}
            />
          </div>
        )}

        {etape === 'verification' && (
          <div>
            <p className="fs-7 mb-3">
              {intl.formatMessage({id: 'TELEPHONIE.PBX.SORTANTS.ACCESSCODE.VERIFICATION_INTRO'})}
            </p>
            {erreur && <div className="text-danger fs-7 mb-3">{erreur}</div>}
            <table className="table table-sm">
              <thead>
                <tr className="table-primary">
                  <th>{intl.formatMessage({id: 'TELEPHONIE.PBX.MONITEUR.TABLE.UTILISATEUR'})}</th>
                  <th>{intl.formatMessage({id: 'TELEPHONIE.PBX.MONITEUR.TABLE.TELEPHONE'})}</th>
                  <th>{intl.formatMessage({id: 'TELEPHONIE.PBX.SORTANTS.ACCESSCODE.ACTUEL'})}</th>
                  <th>{intl.formatMessage({id: 'TELEPHONIE.PBX.SORTANTS.ACCESSCODE.NOUVEAU'})}</th>
                </tr>
              </thead>
              <tbody>
                {utilisateursInvalides.map((u) => (
                  <tr key={u.id}>
                    <td>
                      {u.prenom} {u.nom}
                    </td>
                    <td>{u.telephone}</td>
                    <td>{u.numeroCourtActuel}</td>
                    <td>
                      <Form.Control
                        size="sm"
                        style={{maxWidth: 100}}
                        value={valeurs[u.id] ?? ''}
                        onChange={(e) => setValeurs({...valeurs, [u.id]: e.target.value})}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="d-flex justify-content-end gap-3">
              <BootstrapButton variant="secondary" onClick={terminer}>
                {intl.formatMessage({id: 'CANCEL'})}
              </BootstrapButton>
              <Button
                label={intl.formatMessage({id: 'TELEPHONIE.PBX.SORTANTS.ACCESSCODE.SUIVANT'})}
                onClick={validerVerification}
              />
            </div>
          </div>
        )}

        {etape === 'recapitulatif' && (
          <div>
            {utilisateursInvalides.length > 0 && (
              <table className="table table-sm mb-3">
                <thead>
                  <tr className="table-primary">
                    <th>{intl.formatMessage({id: 'TELEPHONIE.PBX.MONITEUR.TABLE.UTILISATEUR'})}</th>
                    <th>{intl.formatMessage({id: 'TELEPHONIE.PBX.SORTANTS.ACCESSCODE.ACTUEL'})}</th>
                    <th>{intl.formatMessage({id: 'TELEPHONIE.PBX.SORTANTS.ACCESSCODE.NOUVEAU'})}</th>
                  </tr>
                </thead>
                <tbody>
                  {utilisateursInvalides.map((u) => (
                    <tr key={u.id}>
                      <td>
                        {u.prenom} {u.nom}
                      </td>
                      <td>{u.numeroCourtActuel}</td>
                      <td>{valeurs[u.id]}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
            {utilisateursInvalides.length > 0 && (
              <Form.Check
                className="mb-3"
                type="checkbox"
                id="accepte-changements"
                label={intl.formatMessage({id: 'TELEPHONIE.PBX.SORTANTS.ACCESSCODE.ACCEPTER'})}
                checked={accepte}
                onChange={(e) => setAccepte(e.target.checked)}
              />
            )}
            <div className="d-flex justify-content-end gap-3">
              <BootstrapButton variant="secondary" onClick={terminer}>
                {intl.formatMessage({id: 'CANCEL'})}
              </BootstrapButton>
              <Button
                label={intl.formatMessage({id: 'TELEPHONIE.SVI.MENU.FORM.SAVE'})}
                disabled={utilisateursInvalides.length > 0 && !accepte}
                onClick={confirmer}
              />
            </div>
          </div>
        )}

        {etape === 'confirmation' && (
          <div>
            <div className="bg-primary text-white rounded p-3 mb-3 fs-7">
              <i className="bi bi-check-circle-fill me-2" />
              {intl.formatMessage({id: 'TELEPHONIE.SVI.SUCCESS_MESSAGE'})}
            </div>
            <Button
              label={intl.formatMessage({id: 'TELEPHONIE.PBX.SORTANTS.ACCESSCODE.TERMINER'})}
              onClick={terminer}
            />
          </div>
        )}
      </Card.Body>
    </Card>
  );
};

const Clir = (): JSX.Element | null => {
  const intl = useIntl();
  const queryClient = useQueryClient();
  const {data: numerosData, isLoading: isLoadingNumeros} = useGetVirtualPbxNumeros();
  const {data: activiteData, isLoading: isLoadingActivite} = useGetVirtualPbxActivite();
  const {data: assignations, isLoading: isLoadingAssignations} = useGetClirAssignations();
  const [numeroSelectionne, setNumeroSelectionne] = useState<string | null>(null);
  const [recherche, setRecherche] = useState('');
  const [selectionGauche, setSelectionGauche] = useState<Set<string>>(new Set());
  const [selectionDroite, setSelectionDroite] = useState<Set<string>>(new Set());

  if (
    isLoadingNumeros ||
    !numerosData ||
    isLoadingActivite ||
    !activiteData ||
    isLoadingAssignations ||
    !assignations
  ) {
    return null;
  }

  if (numerosData.numerosGlobaux.length === 0) {
    return null;
  }

  const numero = numeroSelectionne ?? numerosData.numerosGlobaux[0].numero;

  const correspond = (telephone: string): boolean =>
    recherche.trim() === '' || telephone.toLowerCase().includes(recherche.trim().toLowerCase());

  const membres = assignations.filter((a) => a.numeroAffiche === numero);
  const utilisateurs = activiteData.lignes.filter(
    (ligne) => !membres.some((m) => m.telephone === ligne.telephone) && correspond(ligne.telephone)
  );

  const basculer = (set: Set<string>, setSet: (set: Set<string>) => void, valeur: string): void => {
    const suivant = new Set(set);
    if (suivant.has(valeur)) {
      suivant.delete(valeur);
    } else {
      suivant.add(valeur);
    }
    setSet(suivant);
  };

  const ajouterMembres = (): void => {
    const misAJour = assignations.map((a) =>
      selectionGauche.has(a.telephone) ? {...a, numeroAffiche: numero} : a
    );
    queryClient.setQueryData(CLIR_ASSIGNATIONS_QUERY_KEY, misAJour);
    setSelectionGauche(new Set());
  };

  const retirerMembres = (): void => {
    const misAJour = assignations.map((a) =>
      selectionDroite.has(a.telephone) ? {...a, numeroAffiche: null} : a
    );
    queryClient.setQueryData(CLIR_ASSIGNATIONS_QUERY_KEY, misAJour);
    setSelectionDroite(new Set());
  };

  return (
    <Card bg="light">
      <Card.Body>
        <div className="fw-bold text-uppercase fs-6 mb-2">
          {intl.formatMessage({id: 'TELEPHONIE.PBX.SORTANTS.CLIR.TITLE'})}
        </div>
        <p className="text-muted fs-7 mb-4">
          {intl.formatMessage({id: 'TELEPHONIE.PBX.SORTANTS.CLIR.DESCRIPTION'})}
        </p>

        <Form.Label className="fw-bold fs-6">
          {intl.formatMessage({id: 'TELEPHONIE.PBX.SORTANTS.CLIR.NUMERO'})}
        </Form.Label>
        <Form.Select
          className="mb-4"
          style={{maxWidth: 320}}
          value={numero}
          onChange={(e) => setNumeroSelectionne(e.target.value)}
        >
          {numerosData.numerosGlobaux.map((n) => (
            <option key={n.id} value={n.numero}>
              {n.numeroFormate} ({n.nom})
            </option>
          ))}
        </Form.Select>

        <Form.Control
          className="mb-4"
          style={{maxWidth: 320}}
          placeholder={intl.formatMessage({id: 'TELEPHONIE.PBX.SORTANTS.CLIR.RECHERCHER'})}
          value={recherche}
          onChange={(e) => setRecherche(e.target.value)}
        />

        <div className="d-flex align-items-center gap-4">
          <div style={{flex: 1}}>
            <div className="fw-bold fs-6 mb-2">
              {intl.formatMessage({id: 'TELEPHONIE.PBX.GROUPES.MEMBRES.UTILISATEURS'})}
            </div>
            <div className="border rounded bg-white p-2" style={{height: 220, overflowY: 'auto'}}>
              {utilisateurs.map((ligne) => (
                <Form.Check
                  key={ligne.telephone}
                  type="checkbox"
                  id={`clir-utilisateur-${ligne.telephone}`}
                  label={
                    <span>
                      <i className="bi bi-telephone-fill me-2 text-muted" />
                      {ligne.telephone}
                    </span>
                  }
                  checked={selectionGauche.has(ligne.telephone)}
                  onChange={() => basculer(selectionGauche, setSelectionGauche, ligne.telephone)}
                />
              ))}
            </div>
          </div>
          <div className="d-flex flex-column gap-3">
            <BootstrapButton
              variant="primary"
              size="sm"
              disabled={selectionGauche.size === 0}
              onClick={ajouterMembres}
            >
              <i className="bi bi-chevron-double-right" />
            </BootstrapButton>
            <BootstrapButton
              variant="primary"
              size="sm"
              disabled={selectionDroite.size === 0}
              onClick={retirerMembres}
            >
              <i className="bi bi-chevron-double-left" />
            </BootstrapButton>
          </div>
          <div style={{flex: 1}}>
            <div className="fw-bold fs-6 mb-2">
              {intl.formatMessage({id: 'TELEPHONIE.PBX.SORTANTS.CLIR.MEMBRES'})}
            </div>
            <div className="border rounded bg-white p-2" style={{height: 220, overflowY: 'auto'}}>
              {membres.map((m) => (
                <Form.Check
                  key={m.telephone}
                  type="checkbox"
                  id={`clir-membre-${m.telephone}`}
                  label={
                    <span>
                      <i className="bi bi-telephone-fill me-2 text-muted" />
                      {m.telephone}
                    </span>
                  }
                  checked={selectionDroite.has(m.telephone)}
                  onChange={() => basculer(selectionDroite, setSelectionDroite, m.telephone)}
                />
              ))}
            </div>
          </div>
        </div>

        <div className="bg-primary text-white rounded p-3 mt-4 fs-7">
          {membres.length === 0
            ? intl.formatMessage({id: 'TELEPHONIE.PBX.SORTANTS.CLIR.AUCUN_MEMBRE'})
            : intl.formatMessage(
                {id: 'TELEPHONIE.PBX.SORTANTS.CLIR.RESUME'},
                {count: membres.length}
              )}
        </div>
      </Card.Body>
    </Card>
  );
};

const AppelsSortants = (): JSX.Element => (
  <div>
    <AccessCodeWizard />
    <Clir />
  </div>
);

export default AppelsSortants;
